// Chat DAG — deterministic vertical-spine + rightward-tool-fan layout.
//
// Main track (user marker → LLM rounds → assistant marker) pins at
// x=0. Each round's tool calls branch RIGHT as leaves (round → tool
// edges exit the round's right handle into the tool's left handle).
// consult_peers' peer children spread on a symmetric polar arc around
// the horizontal horizon through the tool's center; the round's y is
// pushed down before placement to reserve headroom for the upper half
// of the arc so it doesn't crash into the previous round band.

import type { Node, Edge } from 'reactflow';
import type { ChatDagNodeType } from '../types';
import type { NodeDims } from '../../../../contexts/ChatDagDimensionsContext';

// v0.4 Task 8: first-2s RICH_DEFAULT authoritative window. Until
// this many ms elapse after an in-flight turn started, resolveDims
// returns RICH_DEFAULT even if ResizeObserver has measured something
// — prevents the card from popping between RICH_DEFAULT and its
// eventual measured height as content streams in.
export const IN_FLIGHT_RICH_AUTHORITATIVE_MS = 2000;

// Compact defaults — the v0.3 canonical sizes. Non-focal nodes and
// non-dispatched structural kinds render at these dimensions; the
// focal turn (when post-2s window) uses measured values from the
// dim map, falling back to RICH_DEFAULT.
export const COMPACT_DEFAULT: Record<ChatDagNodeType, NodeDims> = {
  // Slight breath after the persona-card / nodes lift earlier this
  // session — turn_marker carries hover-only chrome but reserves a
  // touch more vertical so its baseline doesn't crowd the next round.
  turn_marker: { w: 260, h: 120 },
  // Non-synth llm_round: 200→220 to fit the tool-name list line
  // ("→ search · roster · attention_items"). Height stays tight.
  llm_round: { w: 220, h: 95 },
  // tool_call: 72→90 to fit the new "args:" line below the pill that
  // landed in the tool-node QoL pass.
  tool_call: { w: 280, h: 90 },
  // peer: 220×102 → 240×140 — peer card now hosts asks: + says: +
  // 4-line response + stats line. Old 102px envelope was clipping
  // the AABB even though the renderer painted the full card.
  peer: { w: 240, h: 140 },
  fan_collapsed_chip: { w: 220, h: 70 },
  // phase_stack_group dimensions are computed dynamically per-turn
  // in the post-layout pass; placeholder here so records stay
  // exhaustive.
  phase_stack_group: { w: 0, h: 0 },
  // collapsed_phase_stack card is a fixed-size badge.
  collapsed_phase_stack: { w: 320, h: 90 },
  // turn_separator is a thin horizontal divider rendered between
  // adjacent turns by the post-pass below. Wide enough to span past
  // the spine + a typical tool fan; height is just the rule + label
  // line.
  turn_separator: { w: 540, h: 24 },
};

// Synthesis llm_round override — wider to host the markdown answer
// preview without re-wrapping, and ~60% taller because the delivery
// card carries header + ✓ delivered badge + model + meta line +
// markdown answer (4 lines). makeDimsResolver AND nodeDims() branch
// on `data.isSynthesis` to return this instead of the compact default
// so the layout reserves enough vertical real estate for the
// synthesis landmark on the trail — and so the phase_stack_group
// AABB post-pass doesn't undershoot the synth node either.
export const COMPACT_SYNTH_ROUND: NodeDims = { w: 240, h: 155 };

// Rich defaults — starting dimensions for focal-rendered cards.
// Used during the first-2s authoritative window, and as a fallback
// after that window when the ResizeObserver hasn't measured the
// node yet. Structural kinds that don't participate in focal
// dispatch (fan_collapsed_chip, phase_stack_group, collapsed_phase
// _stack) alias COMPACT — they never flower.
export const RICH_DEFAULT: Record<ChatDagNodeType, NodeDims> = {
  turn_marker: { w: 360, h: 280 },
  llm_round: { w: 260, h: 180 },
  tool_call: { w: 320, h: 280 },
  peer: { w: 360, h: 440 },
  fan_collapsed_chip: COMPACT_DEFAULT.fan_collapsed_chip, // non-dispatched
  phase_stack_group: COMPACT_DEFAULT.phase_stack_group, //   non-dispatched
  collapsed_phase_stack: COMPACT_DEFAULT.collapsed_phase_stack, // non-dispatched
  turn_separator: COMPACT_DEFAULT.turn_separator,         // non-dispatched
};

// Back-compat aliases: several layout helpers below indexed by type
// into the old objects. Kept as a view of COMPACT_DEFAULT so existing
// per-type reads still work unchanged.
const NODE_WIDTH_BY_TYPE: Record<ChatDagNodeType, number> = {
  turn_marker: COMPACT_DEFAULT.turn_marker.w,
  llm_round: COMPACT_DEFAULT.llm_round.w,
  tool_call: COMPACT_DEFAULT.tool_call.w,
  peer: COMPACT_DEFAULT.peer.w,
  fan_collapsed_chip: COMPACT_DEFAULT.fan_collapsed_chip.w,
  phase_stack_group: COMPACT_DEFAULT.phase_stack_group.w,
  collapsed_phase_stack: COMPACT_DEFAULT.collapsed_phase_stack.w,
  turn_separator: COMPACT_DEFAULT.turn_separator.w,
};
const NODE_HEIGHT_BY_TYPE: Record<ChatDagNodeType, number> = {
  turn_marker: COMPACT_DEFAULT.turn_marker.h,
  llm_round: COMPACT_DEFAULT.llm_round.h,
  tool_call: COMPACT_DEFAULT.tool_call.h,
  peer: COMPACT_DEFAULT.peer.h,
  fan_collapsed_chip: COMPACT_DEFAULT.fan_collapsed_chip.h,
  phase_stack_group: COMPACT_DEFAULT.phase_stack_group.h,
  collapsed_phase_stack: COMPACT_DEFAULT.collapsed_phase_stack.h,
  turn_separator: COMPACT_DEFAULT.turn_separator.h,
};

// Focal-aware dim resolver. Decides what (w, h) the layout should
// reserve for a node, given focal state + measured dimensions +
// in-flight window.
export function resolveDims(
  nodeId: string,
  kind: ChatDagNodeType,
  dims: Map<string, NodeDims>,
  focalTurnId: string | null,
  turnKey: string | null | undefined,
  inFlightStartedAtMs: number | null,
): NodeDims {
  const isFocal = focalTurnId != null && turnKey === focalTurnId;
  if (!isFocal) return COMPACT_DEFAULT[kind];
  if (
    inFlightStartedAtMs != null &&
    Date.now() - inFlightStartedAtMs < IN_FLIGHT_RICH_AUTHORITATIVE_MS
  ) {
    return RICH_DEFAULT[kind];
  }
  return dims.get(nodeId) ?? RICH_DEFAULT[kind];
}

// Padding around each turn's "phase stack" bounding box. Real
// constraint isn't ROUND_VERTICAL_GAP (the historical comment was
// over-cautious) — it's TURN_VERTICAL_GAP, which is the actual
// distance between the user/asst markers and the stack's first/last
// child. As long as PHASE_STACK_PADDING_Y stays well under
// TURN_VERTICAL_GAP, the outline can't bleed past the markers.
// 2026-04-25 (later): bumped 24→36 / 16→26 for more inside breathing
// room around the lifted nodes — turns now feel framed, not packed.
const PHASE_STACK_PADDING_X = 36;
const PHASE_STACK_PADDING_Y = 26;

// 2026-04-25: gap constants loosened across the board so the lifted
// nodes (synth round, peer cards with asks/says, tool nodes with
// args lines, turn markers) read with airy spacing rather than
// shoulder-to-shoulder packing. TURN_VERTICAL_GAP held generously
// above PHASE_STACK_PADDING_Y so the stack outline can never bleed
// past the user/asst markers above and below.
const CONSTANTS = {
  TURN_VERTICAL_GAP: 64,        // 32 → 44 → 64 — more outside-buffer
                                // between adjacent phase stacks now
                                // that inside padding bumped to 26.
  ROUND_VERTICAL_GAP: 22,       // 16 → 22
  // Extra cushion between the LAST round's bottom and the assistant
  // marker. ROUND_VERTICAL_GAP (22) is for round → round; the asst
  // marker is the turn's "delivery slot" and earns more breathing
  // room so it doesn't kiss the synth round / phase-stack outline
  // above it. With PHASE_STACK_PADDING_Y=26, ASST_PRELUDE_GAP=56
  // leaves ~30px between the stack outline's bottom edge and the
  // asst marker — visibly framed, not crowded.
  ASST_PRELUDE_GAP: 56,
  TOOL_HORIZONTAL_GAP: 28,      // 24 → 28
  // Vertical gap between stacked tool_calls off a single round. Tools
  // fan VERTICALLY (stacked down-right) not horizontally so that the
  // round's single right-handle can feed each tool's left-handle with
  // its own unique row — avoids edges crossing through intermediate
  // tools when a round has N≥2 tool calls.
  TOOL_VERTICAL_GAP: 20,        // 16 → 20
  TOOL_GUTTER: 56,              // 48 → 56
  // Peer fan symmetric around the horizontal "horizon" through the
  // parent tool's center — peers spread ±arcSpan/2. Per-round layout
  // reserves upper-arc clearance so peers don't crash into the
  // previous round band.
  PEER_ARC_SPAN_DEG: 120,
  PEER_POLAR_RADIUS_MIN: 180,
  PEER_POLAR_RADIUS_MAX: 400,
};

// Compact-only dim lookup, used by paths that don't have access to
// focal context (the phase_stack_group post-pass, for example, runs
// after all positions are set and just needs per-node bounding
// boxes to compute backdrop extents — which it derives from the
// compact defaults plus the already-positioned nodes' explicit
// widths/heights via node.width/node.height).
//
// Synthesis llm_round branch mirrors makeDimsResolver — the AABB
// pass MUST see the synth's true reserved size (240×155) or the
// phase-stack outline will undershoot and clip the delivery card.
function nodeDims(n: Node): { w: number; h: number } {
  const explicitW = (n as any).width as number | undefined;
  const explicitH = (n as any).height as number | undefined;
  if (explicitW != null && explicitH != null) {
    return { w: explicitW, h: explicitH };
  }
  const t = (n.type as ChatDagNodeType) ?? 'tool_call';
  if (t === 'llm_round' && (n.data as any)?.isSynthesis) {
    return COMPACT_SYNTH_ROUND;
  }
  return {
    w: NODE_WIDTH_BY_TYPE[t] ?? 220,
    h: NODE_HEIGHT_BY_TYPE[t] ?? 70,
  };
}

// Build a per-node dim resolver closed over the focal context. All
// layout math inside layoutChatDag goes through this — it's the
// single point where non-focal turns stay compact and the focal
// turn expands to rich.
function makeDimsResolver(
  nodes: Node[],
  dimensions: Map<string, NodeDims>,
  focalTurnId: string | null,
  inFlightStartedAtMs: number | null,
) {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  return (node: Node): NodeDims => {
    const t = (node.type as ChatDagNodeType) ?? 'tool_call';
    // phase_stack_group has dynamic dims set post-layout; keep its
    // existing sentinel zeros so the group pass recomputes cleanly.
    if (t === 'phase_stack_group') return { w: 0, h: 0 };
    // Synthesis llm_rounds carry the markdown answer preview + meta +
    // ✓ delivered badge — much taller than a regular round. Branch
    // here so layout reserves the right vertical space; the renderer
    // owns the actual content.
    if (t === 'llm_round' && (node.data as any)?.isSynthesis) {
      return COMPACT_SYNTH_ROUND;
    }
    const turnKey =
      ((node.data as any)?.turnKey as string | undefined) ?? null;
    return resolveDims(
      node.id,
      t,
      dimensions,
      focalTurnId,
      turnKey,
      inFlightStartedAtMs,
    );
    // byId reference kept so future resolvers can walk siblings if needed.
    void byId;
  };
}

// Arc radius scales so peers don't visually crowd each other along
// the sweep. Clamped to [MIN, MAX] — low peer counts hit MIN for a
// tight-ish arc; high peer counts hit MAX (the chip substitution at
// >3 peers covers the dense case in practice). Used by both the arc
// emitter AND the round-layout pre-pass that reserves upper-arc
// clearance, so the two can never drift.
// Peer dim taken as a parameter so the arc can scale with rich-
// peer heights (440px) when the focal turn's peers render rich.
export function computeArcRadius(N: number, peerH: number): number {
  if (N <= 1) return CONSTANTS.PEER_POLAR_RADIUS_MIN;
  const arcSpan = CONSTANTS.PEER_ARC_SPAN_DEG;
  const stepRad = (arcSpan / (N - 1)) * (Math.PI / 180);
  return Math.max(
    CONSTANTS.PEER_POLAR_RADIUS_MIN,
    Math.min(
      CONSTANTS.PEER_POLAR_RADIUS_MAX,
      (peerH * 1.3) / (2 * Math.sin(stepRad / 2 || 1)),
    ),
  );
}

function layoutPolarArc(
  parent: Node,
  peers: Node[],
  dimsOf: (n: Node) => NodeDims,
): void {
  const N = peers.length;
  if (N === 0) return;
  const { w: pW, h: pH } = dimsOf(parent);
  const rightEdgeX = parent.position.x + pW;
  const centerY = parent.position.y + pH / 2;

  if (N === 1) {
    // Single fan child (typically a fan_collapsed_chip summary badge)
    // → place directly adjacent to the parent with just a small gap.
    const child = peers[0];
    const { h: cH } = dimsOf(child);
    child.position = {
      x: rightEdgeX + CONSTANTS.TOOL_HORIZONTAL_GAP,
      y: centerY - cH / 2,
    };
    return;
  }

  // Symmetric arc centered on the horizon. Arc radius scales with
  // the MAX peer height across this fan — rich peers (440px) push
  // the arc wider so neighbours don't overlap.
  const arcSpan = CONSTANTS.PEER_ARC_SPAN_DEG;
  const peerHMax = Math.max(...peers.map((p) => dimsOf(p).h));
  const radius = computeArcRadius(N, peerHMax);
  const startDeg = -arcSpan / 2;

  peers.forEach((peer, i) => {
    const { w: peerW, h: peerHpx } = dimsOf(peer);
    const t = i / (N - 1);
    const deg = startDeg + arcSpan * t;
    const rad = (deg * Math.PI) / 180;
    peer.position = {
      x: rightEdgeX + radius * Math.cos(rad) - peerW / 2,
      y: centerY + radius * Math.sin(rad) - peerHpx / 2,
    };
  });
}

export interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
  turnBoundingBoxes: Map<
    string,
    { x: number; y: number; w: number; h: number }
  >;
}

export function layoutChatDag(
  nodes: Node[],
  edges: Edge[],
  dimensions: Map<string, NodeDims> = new Map(),
  focalTurnId: string | null = null,
  inFlightStartedAtMs: number | null = null,
): LayoutResult {
  // Build adjacency for "children of parent" lookups (tool → peer
  // fan). Positions computed in one pass per turn; main-track nodes
  // (turn markers + rounds + the per-turn assistant marker) pin at
  // x=0, tool calls extend rightward with a per-round row height
  // driven by the widest/tallest peer fan in that row.
  const childrenByParent = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!childrenByParent.has(e.source)) childrenByParent.set(e.source, []);
    childrenByParent.get(e.source)!.push(e.target);
  });
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  const dimsOf = makeDimsResolver(
    nodes,
    dimensions,
    focalTurnId,
    inFlightStartedAtMs,
  );

  // Group nodes into turn subgraphs. Turn boundary: each user marker
  // starts a new turn; it includes the next N rounds + tools + peers
  // + assistant marker until the next user marker.
  const userMarkers = nodes.filter(
    (n) => n.type === 'turn_marker' && (n.data as any)?.role === 'user',
  );
  userMarkers.sort(
    (a, b) => ((a.data as any).turnIndex ?? 0) - ((b.data as any).turnIndex ?? 0),
  );

  let y = 0;
  userMarkers.forEach((uNode, uIdx) => {
    const { h: uH } = dimsOf(uNode);
    const turnTop = y + (uIdx > 0 ? CONSTANTS.TURN_VERTICAL_GAP : 0);
    uNode.position = { x: 0, y: turnTop };
    y = turnTop + uH + CONSTANTS.ROUND_VERTICAL_GAP;

    const turnIndex = (uNode.data as any).turnIndex ?? uIdx;
    const turnKey =
      ((uNode.data as any).turnKey as string | undefined) ??
      ((uNode.data as any).messageId as string | undefined) ??
      null;

    // If this turn is collapsed, the processor emitted a single
    // `collapsed_phase_stack` card in place of the rounds/tools/peers.
    // Position it on the spine between the user and asst markers and
    // skip the rounds loop entirely.
    const collapsedCard = turnKey
      ? nodes.find(
          (n) =>
            n.type === 'collapsed_phase_stack' &&
            ((n.data as any).turnKey as string | undefined) === turnKey,
        )
      : null;
    if (collapsedCard) {
      const { w: cW, h: cH } = dimsOf(collapsedCard);
      // Center the card under the spine. The spine width is the user
      // marker's width — which could be rich (360) for the focal
      // turn — so we key off dimsOf(uNode), not the static compact.
      const markerW = dimsOf(uNode).w;
      collapsedCard.position = { x: (markerW - cW) / 2, y };
      y += cH + CONSTANTS.ROUND_VERTICAL_GAP;

      const asstNode = nodes.find(
        (n) =>
          n.type === 'turn_marker' &&
          (n.data as any)?.role === 'assistant' &&
          (n.data as any).turnIndex === turnIndex,
      );
      if (asstNode) {
        // The collapsed-card branch already advanced y by the card's
        // height + ROUND_VERTICAL_GAP. Top up to the full
        // ASST_PRELUDE_GAP so the asst marker sits with the same
        // generous breathing room as a normal-turn delivery.
        y += Math.max(
          0,
          CONSTANTS.ASST_PRELUDE_GAP - CONSTANTS.ROUND_VERTICAL_GAP,
        );
        const { h: aH } = dimsOf(asstNode);
        asstNode.position = { x: 0, y };
        y += aH;
      }
      return; // next turn
    }

    const rounds = nodes
      .filter(
        (n) => n.type === 'llm_round' && (n.data as any).turnIndex === turnIndex,
      )
      .sort((a, b) => ((a.data as any).round ?? 0) - ((b.data as any).round ?? 0));

    rounds.forEach((rNode) => {
      const { w: rW, h: rH } = dimsOf(rNode);

      const toolIds = (childrenByParent.get(rNode.id) ?? []).filter(
        (id) => byId.get(id)?.type === 'tool_call',
      );
      // Per-tool vertical step within this round's tool stack. Sizes
      // off the max tool height in the round (rich tool is 280 vs
      // compact 72) so rich tools don't visually stack on top of
      // each other. `toolStep` is computed per-round rather than as a
      // global constant — each tool may have different dims.
      const toolHMax = toolIds.length
        ? Math.max(
            ...toolIds.map((id) => {
              const tc = byId.get(id);
              return tc ? dimsOf(tc).h : NODE_HEIGHT_BY_TYPE.tool_call;
            }),
          )
        : NODE_HEIGHT_BY_TYPE.tool_call;
      const toolStep = toolHMax + CONSTANTS.TOOL_VERTICAL_GAP;

      // Pre-compute the maximum upper-arc overhead across this round's
      // tools so we can push the round DOWN enough to leave room for
      // the upper half of any peer fan. A fan child is either a peer
      // node (full-peer expansion) or a fan_collapsed_chip
      // (density-collapsed stand-in).
      let upperOverhead = 0;
      toolIds.forEach((tcId, tcIdx) => {
        const tc = byId.get(tcId);
        if (!tc) return;
        const fanIds = (childrenByParent.get(tcId) ?? []).filter((id) => {
          const t = byId.get(id)?.type;
          return t === 'peer' || t === 'fan_collapsed_chip';
        });
        if (fanIds.length === 0) return;
        const N = fanIds.length;
        // Arc radius tracks the MAX peer height in this fan — rich
        // peers push the arc wider than compact.
        const peerHMax = Math.max(
          ...fanIds.map((id) => {
            const child = byId.get(id);
            return child
              ? dimsOf(child).h
              : NODE_HEIGHT_BY_TYPE.peer;
          }),
        );
        const r = computeArcRadius(N, peerHMax);
        // For N=1 the single child sits at deg=0 (horizon); for N>=2
        // the topmost child is at -arcSpan/2.
        const topSin =
          N === 1
            ? 0
            : Math.sin((CONSTANTS.PEER_ARC_SPAN_DEG / 2) * Math.PI / 180);
        // First child's height (resolved via dimsOf so rich peers
        // contribute their full 440).
        const firstChild = byId.get(fanIds[0]);
        const childH = firstChild
          ? dimsOf(firstChild).h
          : NODE_HEIGHT_BY_TYPE.peer;
        const { h: tH } = dimsOf(tc);
        const aboveTool = r * topSin + childH / 2 - tH / 2;
        const toolVerticalOffset = tcIdx * toolStep;
        upperOverhead = Math.max(
          upperOverhead,
          Math.max(0, aboveTool - toolVerticalOffset),
        );
      });
      y += upperOverhead;

      rNode.position = { x: 0, y };
      let rowHeight = rH;

      // Stack tools vertically off the round's right side: each tool
      // pins at the same x (round.right + TOOL_GUTTER), with its y
      // incremented by toolStep. The round's single right handle thus
      // feeds N distinct rows — no edges crossing through intermediate
      // tools like a horizontal row would force.
      const toolX = rW + CONSTANTS.TOOL_GUTTER;
      toolIds.forEach((tcId, tcIdx) => {
        const tc = byId.get(tcId);
        if (!tc) return;
        const { h: tH } = dimsOf(tc);
        const toolY = y + tcIdx * toolStep;
        tc.position = { x: toolX, y: toolY };
        rowHeight = Math.max(rowHeight, toolY + tH - y);

        // Fan children = peers OR a fan_collapsed_chip stand-in.
        const fanIds = (childrenByParent.get(tcId) ?? []).filter((id) => {
          const t = byId.get(id)?.type;
          return t === 'peer' || t === 'fan_collapsed_chip';
        });
        if (fanIds.length > 0) {
          const fanNodes = fanIds
            .map((id) => byId.get(id)!)
            .filter(Boolean);
          layoutPolarArc(tc, fanNodes, dimsOf);
          const arcBottom = Math.max(
            ...fanNodes.map((p) => p.position.y + dimsOf(p).h),
          );
          rowHeight = Math.max(rowHeight, arcBottom - y);
        }
      });

      y += rowHeight + CONSTANTS.ROUND_VERTICAL_GAP;
    });

    const asstNode = nodes.find(
      (n) =>
        n.type === 'turn_marker' &&
        (n.data as any)?.role === 'assistant' &&
        (n.data as any).turnIndex === turnIndex,
    );
    if (asstNode) {
      // After the rounds loop, y already had the last round's
      // ROUND_VERTICAL_GAP added. Top up to the full
      // ASST_PRELUDE_GAP so the asst marker reads as a
      // delivery beat, not just another round.
      y += Math.max(
        0,
        CONSTANTS.ASST_PRELUDE_GAP - CONSTANTS.ROUND_VERTICAL_GAP,
      );
      const { h: aH } = dimsOf(asstNode);
      asstNode.position = { x: 0, y };
      y += aH;
    }
  });

  // Set width/height on all nodes for ReactFlow fitView bounds.
  nodes.forEach((n) => {
    const { w, h } = dimsOf(n);
    (n as any).width = w;
    (n as any).height = h;
  });

  // Post-pass: emit one phase_stack_group backdrop per TURN.
  const groups = buildPhaseStackGroupNodes(nodes);
  // Post-pass: emit horizontal "Turn N" separators between adjacent
  // turns. They sit in the middle of TURN_VERTICAL_GAP and don't
  // participate in any turn's bounding box.
  const separators = buildTurnSeparatorNodes(nodes);
  const out = [...groups, ...separators, ...nodes];

  // Derive per-turn bounding boxes for Task 11's scroll-anchor math.
  // Keyed by turnKey; includes all members (user/asst markers, rounds,
  // tools, peers, phase-stack backdrop).
  const turnBoundingBoxes = new Map<
    string,
    { x: number; y: number; w: number; h: number }
  >();
  const boundsByTurn = new Map<
    string,
    { minX: number; minY: number; maxX: number; maxY: number }
  >();
  for (const n of out) {
    const key = (n.data as any)?.turnKey as string | undefined;
    if (!key) continue;
    const { w, h } = dimsOf(n);
    const cur = boundsByTurn.get(key);
    if (!cur) {
      boundsByTurn.set(key, {
        minX: n.position.x,
        minY: n.position.y,
        maxX: n.position.x + w,
        maxY: n.position.y + h,
      });
    } else {
      cur.minX = Math.min(cur.minX, n.position.x);
      cur.minY = Math.min(cur.minY, n.position.y);
      cur.maxX = Math.max(cur.maxX, n.position.x + w);
      cur.maxY = Math.max(cur.maxY, n.position.y + h);
    }
  }
  boundsByTurn.forEach((b, k) => {
    turnBoundingBoxes.set(k, {
      x: b.minX,
      y: b.minY,
      w: b.maxX - b.minX,
      h: b.maxY - b.minY,
    });
  });

  return { nodes: out, edges, turnBoundingBoxes };
}

// Emit one horizontal trail separator between each adjacent pair of
// turns. Centered in TURN_VERTICAL_GAP, anchored on the spine
// midline (user-marker x-center). Separators are decoration — they
// don't take additional vertical space (they sit IN the gap, not
// shouldering it open) and don't participate in any turn's bounding
// box (no turnKey on the data).
function buildTurnSeparatorNodes(nodes: Node[]): Node[] {
  const userMarkers = nodes
    .filter(
      (n) =>
        n.type === 'turn_marker' && (n.data as any)?.role === 'user',
    )
    .sort(
      (a, b) =>
        ((a.data as any)?.turnIndex ?? 0) -
        ((b.data as any)?.turnIndex ?? 0),
    );
  if (userMarkers.length < 2) return [];

  const sepW = COMPACT_DEFAULT.turn_separator.w;
  const sepH = COMPACT_DEFAULT.turn_separator.h;
  const out: Node[] = [];
  for (let i = 1; i < userMarkers.length; i++) {
    const userNext = userMarkers[i];
    const turnIdx = ((userNext.data as any).turnIndex ?? i) as number;
    const ts = (userNext.data as any).timestamp as string | undefined;
    let label = `Turn ${turnIdx + 1}`;
    if (ts) {
      try {
        const stamp = new Date(ts).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });
        label = `${label} · ${stamp}`;
      } catch {
        // ignore — fall back to bare label
      }
    }
    // Sit in the middle of TURN_VERTICAL_GAP above the next user marker.
    const sepY =
      userNext.position.y -
      CONSTANTS.TURN_VERTICAL_GAP / 2 -
      sepH / 2;
    // Anchor on the spine center (user marker x-midpoint).
    const userW = nodeDims(userNext).w;
    const sepX = userNext.position.x + userW / 2 - sepW / 2;
    out.push({
      id: `sep-turn-${turnIdx}`,
      type: 'turn_separator',
      position: { x: sepX, y: sepY },
      data: {
        kind: 'turn_separator',
        turnIndex: turnIdx,
        label,
        timestamp: ts,
      },
      width: sepW,
      height: sepH,
      draggable: false,
      selectable: false,
    } as Node);
  }
  return out;
}

function buildPhaseStackGroupNodes(nodes: Node[]): Node[] {
  // Bucket every non-marker node by its turnKey — those are the
  // phase-stack members. Skip turns that have a collapsed_phase_stack
  // card: the card already renders its own labelled chrome, and a
  // backdrop box around a single 320x90 card reads as redundant.
  const collapsedTurnKeys = new Set<string>();
  for (const n of nodes) {
    if (n.type === 'collapsed_phase_stack') {
      const key = (n.data as any)?.turnKey as string | undefined;
      if (key) collapsedTurnKeys.add(key);
    }
  }

  const membersByTurnKey = new Map<string, Node[]>();
  const userMarkerByTurnKey = new Map<string, Node>();
  for (const n of nodes) {
    const key = (n.data as any)?.turnKey as string | undefined;
    if (!key) continue;
    if (collapsedTurnKeys.has(key)) continue;
    // Skip turn_marker nodes — they're the spine entry/exit, not
    // inside the phase stack.
    if (n.type === 'turn_marker') {
      if ((n.data as any)?.role === 'user') {
        userMarkerByTurnKey.set(key, n);
      }
      continue;
    }
    if (!membersByTurnKey.has(key)) membersByTurnKey.set(key, []);
    membersByTurnKey.get(key)!.push(n);
  }

  const groups: Node[] = [];
  membersByTurnKey.forEach((members, turnKey) => {
    const userMarker = userMarkerByTurnKey.get(turnKey);
    if (!userMarker) return;
    const turnIndex = (userMarker.data as any).turnIndex ?? 0;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let roundCount = 0;
    let toolCount = 0;
    for (const n of members) {
      if (n.type === 'llm_round') roundCount += 1;
      if (n.type === 'tool_call') toolCount += 1;
      const { w, h } = nodeDims(n);
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + w);
      maxY = Math.max(maxY, n.position.y + h);
    }
    if (!Number.isFinite(minX)) return;

    // Use the user marker's preview as the stack name. The processor
    // also derives one on collapsed_phase_stack; for the group box
    // we use the same source so both renderings read the same.
    // Strip leading markdown chrome (#, *, _, >, list bullets, ` )
    // mirroring deriveStackName in chatDagDataProcessor — keeps
    // `# Brainstorm` from rendering its literal hash on the AABB.
    const previewRaw = ((userMarker.data as any)?.preview ?? '') as string;
    const preview = previewRaw.replace(/^[\s>#*_`+\-]+\s*/, '').trim() || previewRaw;
    const name = preview.length > 48 ? preview.slice(0, 47) + '…' : preview;

    const width = maxX - minX + 2 * PHASE_STACK_PADDING_X;
    const height = maxY - minY + 2 * PHASE_STACK_PADDING_Y;

    groups.push({
      id: `group-stack-${turnKey}`,
      type: 'phase_stack_group',
      position: {
        x: minX - PHASE_STACK_PADDING_X,
        y: minY - PHASE_STACK_PADDING_Y,
      },
      data: {
        kind: 'phase_stack_group',
        turnKey,
        turnIndex,
        name: name || '(empty prompt)',
        roundCount,
        toolCount,
        childCount: members.length,
      },
      // `style.{width, height}` are what ReactFlow applies to the
      // node wrapper's CSS. Without these the inner div's
      // w-full/h-full renders at 0x0 (wrapper auto-sizes to content,
      // and aria-hidden label content with absolute-positioned
      // elements doesn't contribute to size).
      //
      // NO `zIndex: -1` here — z-index:-1 on a ReactFlow node wrapper
      // creates a stacking context that pushes the whole node (INCLUDING
      // the clickable label with pointer-events:auto) behind the
      // ReactFlow pane; clicks on the label hit the pane instead and
      // the handler never fires. DOM ordering is enough: groups are
      // prepended in the layout's return (see `return [...groups, ...
      // nodes]`) so they paint FIRST (behind) while tool/peer/round
      // nodes paint LATER (on top) — same visual layering, no
      // stacking-context trap.
      //
      // Wrapper's pointer events default to `auto` so the entire
      // backdrop is clickable (not just the label). Tool/peer/round
      // nodes are painted AFTER the group in DOM order (groups are
      // prepended in the layout's return) at the same default z-index,
      // so they sit ON TOP: clicks on a tool node still land on the
      // tool node, while clicks on "empty" backdrop area (margins,
      // gaps between rounds, etc.) fall through to the stack's click
      // handler inside PhaseStackGroupNode → focusBubble(turnKey).
      width,
      height,
      style: { width, height },
      selectable: false,
      draggable: false,
      focusable: false,
    } as Node);
  });
  return groups;
}
